import React from "react";
import PopupDialog from "components/dialog/PopupDialog";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { boolText, showEmpty } from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

const emptyForm = {
  id_pengelola_kejuaraan: "",
  id_kejuaraan: "",
  id_cabor: "",
  nik: "",
};

const adminKejuaraanConfig = {
  title: "Admin Kejuaraan",
  addLabel: "Tambah Admin",
  addTitle: "Tambah Admin Kejuaraan",
  editTitle: "Ubah Admin Kejuaraan",
  groupName: "ADMIN_KEJUARAAN",
  assignedMessage: "NIK ini sudah menjadi Admin Kejuaraan terpilih.",
  helperMessage: "Tombol Simpan aktif setelah NIK 16 digit ditemukan dan belum menjadi Admin Kejuaraan terpilih.",
  saveError: "Admin Kejuaraan gagal disimpan.",
};

const operatorCaborConfig = {
  title: "Operator Cabor",
  addLabel: "Tambah Operator",
  addTitle: "Tambah Operator Cabor",
  editTitle: "Ubah Operator Cabor",
  groupName: "OPERATOR_KEJUARAAN_CABOR",
  assignedMessage: "NIK ini sudah menjadi Operator Cabor terpilih.",
  helperMessage: "Tombol Simpan aktif setelah NIK 16 digit ditemukan dan belum menjadi Operator Cabor terpilih.",
  saveError: "Operator Cabor gagal disimpan.",
  contextKejuaraan: true,
  hideStatus: true,
};

function AdminKejuaraanFormDialog({
  show,
  mode = "add",
  initialData = null,
  kejuaraanOptions = [],
  caborOptions = [],
  config = adminKejuaraanConfig,
  closeHandle,
  onSaved,
}) {
  const [showMsg] = useMsgBox();
  const [, , loading, axiosFetch] = useAxiosFunction();
  const [form, setForm] = React.useState(emptyForm);
  const [personal, setPersonal] = React.useState(null);
  const [lookupStatus, setLookupStatus] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const isEdit = mode === "edit";
  const nikReady = String(form.nik || "").trim().length === 16;
  const hasKejuaraan = Boolean(config.contextKejuaraan || form.id_kejuaraan);
  const hasCabor = Boolean(!config.contextKejuaraan || form.id_cabor);
  const canSave = Boolean(hasKejuaraan && hasCabor && personal?.found && !personal?.assigned && !loading && !saving);

  React.useEffect(() => {
    if (!show) return;
    const nextForm = {
      ...emptyForm,
      id_pengelola_kejuaraan: initialData?.id_pengelola_kejuaraan || "",
      id_kejuaraan: initialData?.id_kejuaraan || "",
      id_cabor: initialData?.id_cabor || "",
      nik: initialData?.nik || "",
    };
    setForm(nextForm);
    setPersonal(
      initialData?.id_personal
        ? {
            found: true,
            assigned: false,
            id_personal: initialData.id_personal,
            nik: initialData?.nik || "",
            nama: formatPersonName(initialData, ["personal_name"]),
            hp: initialData?.hp || "",
          }
        : null,
    );
    setLookupStatus("");
    setSaving(false);
  }, [show, initialData]);

  React.useEffect(() => {
    if (!show) return undefined;
    const nik = String(form.nik || "").trim();
    if (!hasKejuaraan || !hasCabor || nik.length < 16) {
      if (!initialData?.id_personal || nik !== String(initialData?.nik || "")) {
        setPersonal(null);
      }
      setLookupStatus(nik.length > 0 && nik.length < 16 ? "NIK harus 16 digit." : "");
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      const resp = await axiosFetch({
        method: "GET",
        url: "/kegiatan/admin-kejuaraan/lookup-personal",
        auth: true,
        params: {
          id_kejuaraan: form.id_kejuaraan,
          id_cabor: form.id_cabor,
          nik,
          exclude_id: form.id_pengelola_kejuaraan || "",
          groupname: config.groupName,
        },
      });
      const data = resp?.data || {};
      setPersonal(data);
      if (!data?.found) {
        setLookupStatus("NIK Personal tidak ditemukan.");
      } else if (data?.assigned) {
        setLookupStatus(config.assignedMessage);
      } else {
        setLookupStatus("");
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [show, hasKejuaraan, hasCabor, form.id_kejuaraan, form.id_cabor, form.nik, form.id_pengelola_kejuaraan, config.groupName]);

  const updateField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    if (name === "id_kejuaraan" || name === "id_cabor" || name === "nik") {
      setLookupStatus("");
      if (!(name === "nik" && value === initialData?.nik)) {
        setPersonal(null);
      }
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    const id = form.id_pengelola_kejuaraan || "";
    const resp = await axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `/kegiatan/pengelola-kejuaraan/${id}` : "/kegiatan/pengelola-kejuaraan",
      auth: true,
      data: {
        id_kejuaraan: form.id_kejuaraan,
        id_cabor: form.id_cabor,
        id_personal: personal?.id_personal || "",
        groupname: config.groupName,
      },
    });
    setSaving(false);
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Data berhasil disimpan.");
      onSaved?.();
      return;
    }
    await showMsg("Error", resp?.message || config.saveError);
  };

  return (
    <PopupDialog
      title={isEdit ? config.editTitle : config.addTitle}
      size="lg"
      show={show}
      closeHandle={closeHandle}
      closeLabel="Batal"
      doHandle={save}
      doLabel={saving ? "Menyimpan..." : "Simpan"}
      doDisabled={!canSave}
    >
      <div className="row">
        {!config.contextKejuaraan ? (
          <div className="form-group col-md-12">
            <label>Kejuaraan</label>
            <select
              className="form-control"
              value={form.id_kejuaraan}
              onChange={(event) => updateField("id_kejuaraan", event.target.value)}
            >
              <option value="">-Pilih Kejuaraan-</option>
              {kejuaraanOptions.map((item) => (
                <option key={item.id_kejuaraan} value={item.id_kejuaraan}>
                  {item.nama}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {config.contextKejuaraan ? (
          <div className="form-group col-md-12">
            <label>Cabor</label>
            <select
              className="form-control"
              value={form.id_cabor}
              onChange={(event) => updateField("id_cabor", event.target.value)}
            >
              <option value="">-Pilih Cabor-</option>
              {caborOptions.map((item) => (
                <option key={item.id_cabor} value={item.id_cabor}>
                  {item.nama}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="form-group col-md-12">
          <label>NIK Personal</label>
          <input
            className="form-control"
            value={form.nik}
            maxLength={16}
            onChange={(event) => updateField("nik", event.target.value.replace(/\D/g, ""))}
            placeholder="Masukkan 16 digit NIK"
          />
          {lookupStatus ? <div className="invalid-feedback d-block">{lookupStatus}</div> : null}
        </div>
        <div className="form-group col-md-6">
          <label>Nama</label>
          <input className="form-control" value={personal?.found ? formatPersonName(personal, ["nama"]) : ""} disabled />
        </div>
        <div className="form-group col-md-6">
          <label>HP</label>
          <input className="form-control" value={personal?.found ? personal?.hp || "" : ""} disabled />
        </div>
        {!hasKejuaraan ? (
          <div className="col-12 text-muted small">Pilih kejuaraan terlebih dahulu, lalu masukkan NIK Personal.</div>
        ) : !hasCabor ? (
          <div className="col-12 text-muted small">Pilih cabor terlebih dahulu, lalu masukkan NIK Personal.</div>
        ) : !nikReady ? (
          <div className="col-12 text-muted small">{config.helperMessage}</div>
        ) : null}
      </div>
    </PopupDialog>
  );
}

function KejuaraanRoleAssignmentPage({ config = adminKejuaraanConfig }) {
  const [, , , axiosFetch] = useAxiosFunction();
  const [kejuaraanResp] = useAxios({ method: "GET", url: "/references/kejuaraan", auth: true });
  const kejuaraanOptions = Array.isArray(kejuaraanResp?.data) ? kejuaraanResp.data : [];
  const [caborOptions, setCaborOptions] = React.useState([]);
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];
  const [formOpen, setFormOpen] = React.useState(false);
  const [formMode, setFormMode] = React.useState("add");
  const [formData, setFormData] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let canceled = false;
    const loadCaborOptions = async () => {
      if (!config.contextKejuaraan) {
        setCaborOptions([]);
        return;
      }
      const resp = await axiosFetch({
        method: "GET",
        url: "/references/kejuaraan-admin-cabor-selected",
        auth: true,
      });
      if (!canceled) {
        setCaborOptions(Array.isArray(resp?.data) ? resp.data : []);
      }
    };
    loadCaborOptions();
    return () => {
      canceled = true;
    };
  }, [config.contextKejuaraan]);

  const openAdd = () => {
    setFormMode("add");
    setFormData(null);
    setFormOpen(true);
  };

  const openEdit = async (item) => {
    const id = item?.id_pengelola_kejuaraan || "";
    const resp = await axiosFetch({
      method: "GET",
      url: `/kegiatan/pengelola-kejuaraan/${id}`,
      auth: true,
    });
    if (!resp?.data) return;
    setFormMode("edit");
    setFormData(resp.data);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormData(null);
  };

  const saved = () => {
    closeForm();
    setReloadKey((current) => current + 1);
  };

  const coltype = {
    id_pengelola_kejuaraan: { type: "hidden", name: "id_pengelola_kejuaraan" },
    kejuaraan_name: {
      label: "Kejuaraan",
      renderFunc: (val) => showEmpty(val),
      width: 260,
    },
    personal_name: {
      label: "Nama",
      renderFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["personal_name"])),
      renderExportFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["personal_name"])),
      width: 220,
    },
    nik: {
      label: "NIK",
      renderFunc: (val) => showEmpty(val),
      renderExportFunc: (val) => showEmpty(val),
      width: config.hideStatus ? 95 : 140,
      align: "center",
    },
    hp: {
      label: "HP",
      renderFunc: (val) => showEmpty(val),
      renderExportFunc: (val) => showEmpty(val),
      width: 120,
      align: "center",
    },
    cabor_name: {
      label: "Cabor",
      renderFunc: (val) => showEmpty(val),
      renderExportFunc: (val) => showEmpty(val),
      width: 180,
    },
    email: {
      label: "Email",
      renderFunc: (val) => showEmpty(val),
      renderExportFunc: (val) => showEmpty(val),
      width: 180,
    },
    aktif: {
      label: "Status",
      renderFunc: (val) => boolText(val),
      renderExportFunc: (val) => boolText(val),
      width: 90,
      align: "center",
    },
    action: { label: "Aksi", align: "center", width: 100 },
  };
  const baseListColumns = config.hideStatus
    ? ["no", "nik", "personal_name", "cabor_name", "hp", "email"]
    : config.contextKejuaraan
    ? ["no", "cabor_name", "personal_name", "nik", "hp"]
    : ["no", "kejuaraan_name", "personal_name", "nik", "hp"];
  const listColumns = config.hideStatus ? [...baseListColumns, "action"] : [...baseListColumns, "aktif", "action"];
  const exportColumns = config.hideStatus ? baseListColumns : [...baseListColumns, "aktif"];
  const filterFields = [
    { dtype: "hidden", name: "groupname", value: config.groupName },
    { dtype: "q", name: "q", colsize: 3 },
    ...(config.contextKejuaraan
      ? [
          {
            dtype: "dropdown",
            name: "id_cabor",
            label: "Cabor",
            colsize: 3,
            options: caborOptions,
            valueId: "id_cabor",
            labelId: "nama",
            placeholder: "-Semua Cabor-",
          },
        ]
      : [
          {
            dtype: "dropdown",
            name: "id_kejuaraan",
            label: "Kejuaraan",
            colsize: 3,
            options: kejuaraanOptions,
            valueId: "id_kejuaraan",
            labelId: "nama",
            placeholder: "-Semua Kejuaraan-",
          },
        ]),
    ...(config.hideStatus
      ? []
      : [
          {
            dtype: "dropdown",
            name: "aktif",
            label: "Status",
            colsize: 3,
            options: statusAktifOptions,
            valueId: "id",
            labelId: "nama",
            placeholder: "-Semua Status-",
          },
        ]),
  ];

  return (
    <>
      <DataList
        title={config.title}
        coltype={coltype}
        reload={reloadKey}
        toolbar={[{ label: config.addLabel, icon: "fa-plus", onClick: openAdd }]}
        dlist={{
          url: "/kegiatan/pengelola-kejuaraan",
          columns: listColumns,
          filter: filterFields,
          sort_by: ["kejuaraan_name", "cabor_name", "personal_name", "nik"],
        }}
        editconfig={{
          editHandle: openEdit,
        }}
        delconfig={{
          url: "/kegiatan/pengelola-kejuaraan",
          cellid: "id_pengelola_kejuaraan",
          cellname: "personal_name",
        }}
        {...(!config.hideStatus
          ? {
              activateconfig: {
                url: "/kegiatan/pengelola-kejuaraan",
                cellid: "id_pengelola_kejuaraan",
                cellname: "personal_name",
                cellactivate: "aktif",
                labelactivate: "Aktifkan",
                labelinactivate: "Non-aktifkan",
                cfmactivate: "mengaktifkan",
                cfminactivate: "menonaktifkan",
                infoactivate: "diaktifkan",
                infoinactivate: "dinonaktifkan",
              },
            }
          : {})}
        pdflist={{ title: config.title, columns: exportColumns }}
        xlslist={{ title: config.title, columns: exportColumns }}
      />
      <AdminKejuaraanFormDialog
        show={formOpen}
        mode={formMode}
        initialData={formData}
        kejuaraanOptions={kejuaraanOptions}
        caborOptions={caborOptions}
        config={config}
        closeHandle={closeForm}
        onSaved={saved}
      />
    </>
  );
}

export function OperatorCaborManagementPage() {
  return <KejuaraanRoleAssignmentPage config={operatorCaborConfig} />;
}

export default function PengelolaKejuaraanManagementPage() {
  return <KejuaraanRoleAssignmentPage config={adminKejuaraanConfig} />;
}
